import { useEffect, useState } from 'react';

/**
 * Countdown — v2 port of v1's countdown.ejs.
 * Counts down to a target date with D : H : M : S digits + bump animation on tick.
 *
 * Config (all optional except targetDate):
 *   targetDate: ISO string                    (required)
 *   title: string                             (default 'Countdown')
 *   accentColor: string                       (default '#8b5cf6')
 *   showDays: boolean                         (default true)
 *   compact: boolean                          (default false — single line when true)
 *   onFinishMessage: string                   (default 'Live now!')
 */

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

interface Segment {
  label: string;
  value: string;
}

function diffSegments(
  target: number,
  now: number,
  showDays: boolean,
): {
  segments: Segment[];
  finished: boolean;
} {
  const diff = Math.max(0, target - now);
  const totalSecs = Math.floor(diff / 1000);
  if (showDays) {
    const d = Math.floor(totalSecs / 86400);
    const h = Math.floor((totalSecs % 86400) / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    return {
      segments: [
        { label: 'Days', value: pad2(d) },
        { label: 'Hours', value: pad2(h) },
        { label: 'Mins', value: pad2(m) },
        { label: 'Secs', value: pad2(s) },
      ],
      finished: diff <= 0,
    };
  }
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return {
    segments: [
      { label: 'Hours', value: pad2(h) },
      { label: 'Mins', value: pad2(m) },
      { label: 'Secs', value: pad2(s) },
    ],
    finished: diff <= 0,
  };
}

export function CountdownWidget({ fields }: { fields: Record<string, unknown> }) {
  const targetDate = fields.targetDate as string | undefined;
  const title = String(fields.title ?? 'Countdown');
  const accent = String(fields.accentColor ?? '#8b5cf6');
  const showDays = (fields.showDays as boolean | undefined) ?? true;
  const compact = (fields.compact as boolean | undefined) ?? false;
  const onFinishMessage = String(fields.onFinishMessage ?? 'Live now!');

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!targetDate) {
    return (
      <div
        style={{
          padding: '14px 20px',
          color: 'rgba(255,255,255,0.3)',
          fontFamily: "'Poppins', system-ui, sans-serif",
          fontSize: 12,
          background: 'rgba(15,12,30,0.85)',
          border: '1px solid rgba(139,92,246,0.18)',
          borderRadius: 10,
        }}
      >
        No countdown configured
      </div>
    );
  }

  const target = new Date(targetDate).getTime();
  const { segments, finished } = diffSegments(target, now, showDays);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(15,12,30,0.92), rgba(22,16,46,0.92))',
        border: `1px solid ${accent}66`,
        borderRadius: 14,
        padding: compact ? '10px 16px' : '14px 22px 16px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        animation: finished ? 'cd-finished-pulse 1.5s ease-in-out infinite' : undefined,
        boxShadow: finished ? `0 0 32px ${accent}66` : undefined,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 24,
          right: 24,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      {!compact && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
            marginBottom: 10,
          }}
        >
          {title}
        </div>
      )}
      {finished ? (
        <div
          style={{
            fontSize: compact ? 22 : 32,
            fontWeight: 900,
            letterSpacing: -0.5,
            color: accent,
            textAlign: 'center',
          }}
        >
          {onFinishMessage}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'baseline',
            gap: compact ? 6 : 14,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {segments.map((seg, i) => (
            <div
              key={seg.label}
              style={{ display: 'flex', alignItems: 'baseline', gap: compact ? 6 : 14 }}
            >
              <Cell value={seg.value} label={seg.label} accent={accent} compact={compact} />
              {i < segments.length - 1 && (
                <span
                  style={{
                    fontSize: compact ? 22 : 30,
                    fontWeight: 700,
                    color: `${accent}66`,
                    lineHeight: 1,
                  }}
                >
                  :
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <style>{`
        @keyframes cd-bump {
          0% { transform: scale(1); }
          30% { transform: scale(1.18); color: ${accent}; }
          60% { transform: scale(0.96); }
          100% { transform: scale(1); }
        }
        @keyframes cd-finished-pulse {
          0%, 100% { box-shadow: 0 0 32px ${accent}66; }
          50% { box-shadow: 0 0 48px ${accent}99; }
        }
      `}</style>
    </div>
  );
}

function Cell({
  value,
  label,
  accent,
  compact,
}: {
  value: string;
  label: string;
  accent: string;
  compact: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span
        key={value}
        style={{
          fontSize: compact ? 22 : 36,
          fontWeight: 900,
          lineHeight: 1,
          color: 'white',
          animation: 'cd-bump 350ms cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        {value}
      </span>
      {!compact && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: `${accent}99`,
            marginTop: 4,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
